// src/store/slices/exhibitionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import exhibitionApi from 'api/exhibitionApi';

export const fetchEntities = createAsyncThunk(
  'exhibition/fetchEntities',
  async ({ type, page = 1, pageSize = 10, search = '', exhibition = '',exhibition_member__exhibition='' }, { rejectWithValue }) => {
    try {
      let data;
      if (type === 'exhibition') data = await exhibitionApi.getAll(page, pageSize, search);
      if (type === 'member') data = await exhibitionApi.getMembers(page, pageSize, search, exhibition);
      if (type === 'assignment') data = await exhibitionApi.getAssignments(page, pageSize, search, exhibition_member__exhibition);
      if (type === 'visitor') data = await exhibitionApi.getVisitors(page, pageSize, search, exhibition);
      return { type, ...data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || `Failed to load ${type}s`);
    }
  }
);

export const createEntity = createAsyncThunk(
  'exhibition/createEntity',
  async ({ type, data }, { rejectWithValue }) => {
    try {
      let response;
      if (type === 'exhibition') response = await exhibitionApi.create(data);
      if (type === 'member') response = await exhibitionApi.createMember(data);
      if (type === 'assignment') response = await exhibitionApi.createAssignment(data);
      if (type === 'visitor') response = await exhibitionApi.createVisitor(data);
      return { type, ...response };
    } catch (err) {
      return rejectWithValue(err.response?.data || `Failed to create ${type}`);
    }
  }
);

// export const bulkCreateEntity = createAsyncThunk(
//   'exhibition/bulkCreateEntity',
//   async ({ type, data }, { rejectWithValue }) => {
//     try {
//       let response;
//       if (type === 'member') {
//         // data is array of { exhibition, employee }
//         response = await exhibitionApi.bulkCreateMembers(data);
//       }
//       // Add other bulk types here if needed later
//       return { type, ...response };
//     } catch (err) {
//       return rejectWithValue(err.response?.data || `Failed to bulk create ${type}`);
//     }
//   }
// );

export const bulkCreateEntity = createAsyncThunk(
  'exhibition/bulkCreateEntity',
  async ({ type, data }, { rejectWithValue }) => {
    try {
      let response;
      if (type === 'member') {
        response = await exhibitionApi.bulkCreateMembers(data);
      }
      // ADD THIS:
      else if (type === 'assignment') {
        response = await exhibitionApi.bulkCreateAssignments(data);
      }
      return { type, ...response };
    } catch (err) {
      return rejectWithValue(err.response?.data || `Failed to bulk create ${type}`);
    }
  }
);

export const updateEntity = createAsyncThunk(
  'exhibition/updateEntity',
  async ({ type, id, data }, { rejectWithValue }) => {
    try {
      let response;
      if (type === 'exhibition') response = await exhibitionApi.update(id, data);
      if (type === 'member') response = await exhibitionApi.updateMember(id, data);
      if (type === 'assignment') response = await exhibitionApi.updateAssignment(id, data);
      if (type === 'visitor') response = await exhibitionApi.updateVisitor(id, data);
      return { type, ...response };
    } catch (err) {
      return rejectWithValue(err.response?.data || `Failed to update ${type}`);
    }
  }
);

export const deleteEntity = createAsyncThunk(
  'exhibition/deleteEntity',
  async ({ type, id }, { rejectWithValue }) => {
    try {
      if (type === 'exhibition') await exhibitionApi.delete(id);
      if (type === 'member') await exhibitionApi.deleteMember(id);
      if (type === 'assignment') await exhibitionApi.deleteAssignment(id);
      if (type === 'visitor') await exhibitionApi.deleteVisitor(id);
      return { type, id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || `Failed to delete ${type}`);
    }
  }
);

export const exportEntities = createAsyncThunk(
  'exhibition/exportEntities',
  async ({ type }, { rejectWithValue }) => {
    try {
      let blob;
      if (type === 'exhibition') blob = await exhibitionApi.exportCsv();
      if (type === 'member') blob = await exhibitionApi.exportMembers();
      if (type === 'assignment') blob = await exhibitionApi.exportAssignments();
      if (type === 'visitor') blob = await exhibitionApi.exportVisitors();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}s_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

export const importPreview = createAsyncThunk(
  'exhibition/importPreview',
  async ({ type, file }, { rejectWithValue }) => {
    try {
      let data;
      if (type === 'exhibition') data = await exhibitionApi.importPreview(file);
      if (type === 'member') data = await exhibitionApi.importMemberPreview(file);
      if (type === 'assignment') data = await exhibitionApi.importAssignmentPreview(file);
      if (type === 'visitor') data = await exhibitionApi.importVisitorPreview(file);
      return { type, data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

export const importConfirm = createAsyncThunk(
  'exhibition/importConfirm',
  async ({ type, preview }, { rejectWithValue, dispatch }) => {
    try {
      if (type === 'exhibition') await exhibitionApi.importConfirm(preview);
      if (type === 'member') await exhibitionApi.importMemberConfirm(preview);
      if (type === 'assignment') await exhibitionApi.importAssignmentConfirm(preview);
      if (type === 'visitor') await exhibitionApi.importVisitorConfirm(preview);
      dispatch(fetchEntities({ type, page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

// ────────────────────────────────────────────────
// Assignment Actions
// ────────────────────────────────────────────────
export const acknowledgeAssignment = createAsyncThunk(
  'exhibition/acknowledgeAssignment',
  async ({ id, is_accepted }, { rejectWithValue }) => {
    try {
      const response = await exhibitionApi.acknowledge(id, is_accepted);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error);
    }
  }
);

export const requestReturnAssignment = createAsyncThunk(
  'exhibition/requestReturnAssignment',
  async ({ id, return_quantity }, { rejectWithValue }) => {
    try {
      const response = await exhibitionApi.requestReturn(id, return_quantity);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to request return');
    }
  }
);

export const confirmReturnAssignment = createAsyncThunk(
  'exhibition/confirmReturnAssignment',
  async ({ id, is_accepted }, { rejectWithValue }) => {
    try {
      const response = await exhibitionApi.confirmReturn(id, is_accepted);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to confirm return');
    }
  }
);

const exhibitionSlice = createSlice({
  name: 'exhibition',
  initialState: {
    exhibitions: [],
    paginationExhibitions: { count: 0, page_size: 10 },
    members: [],
    paginationMembers: { count: 0, page_size: 10 },
    assignments: [],
    paginationAssignments: { count: 0, page_size: 10 },
    visitors: [],
    paginationVisitors: { count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntities.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, data, pagination } = action.payload;
        state[`${type}s`] = data;
        state[`pagination${type.charAt(0).toUpperCase() + type.slice(1)}s`] = pagination;
      })
      .addCase(fetchEntities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createEntity.fulfilled, (state, action) => {
        const { type } = action.payload;
        state[`${type}s`].unshift(action.payload);
        state[`pagination${type.charAt(0).toUpperCase() + type.slice(1)}s`].count += 1;
      })
      .addCase(updateEntity.fulfilled, (state, action) => {
        const { type } = action.payload;
        const idx = state[`${type}s`].findIndex(e => e.id === action.payload.id);
        if (idx !== -1) state[`${type}s`][idx] = action.payload;
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        const { type, id } = action.payload;
        state[`${type}s`] = state[`${type}s`].filter(e => e.id !== id);
        state[`pagination${type.charAt(0).toUpperCase() + type.slice(1)}s`].count -= 1;
      })
      .addCase(exportEntities.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(importPreview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importPreview.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(importPreview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(importConfirm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importConfirm.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(importConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // ────────────────────────────────────────────────
      // Assignment Actions
      // ────────────────────────────────────────────────
      .addMatcher(
        (action) =>
          action.type.endsWith('/fulfilled') &&
          [
            'exhibition/acknowledgeAssignment',
            'exhibition/requestReturnAssignment',
            'exhibition/confirmReturnAssignment',
          ].includes(action.type),
        (state, action) => {
          const idx = state.assignments.findIndex(a => a.id === action.payload.id);
          if (idx !== -1) {
            state.assignments[idx] = { ...state.assignments[idx], ...action.payload };
          }
        }
      );
  },
});

export const { clearError } = exhibitionSlice.actions;
export default exhibitionSlice.reducer;
